import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import business from 'moment-business'
import { IconContext } from 'react-icons'
import { IoIosArrowBack, IoMdCloseCircle, IoMdInformationCircleOutline, IoMdUndo } from 'react-icons/io'
import _ from 'lodash'
import { Alert } from '@wellbeingxltd/fe-lib/components'
import { DELIVERY_COMPANIES, GET_STORE } from '@wellbeingxltd/fe-lib/constants'
import { formatPrice, getTransactionStateText } from '@wellbeingxltd/fe-lib/helpers'
import { useConfirm } from '@wellbeingxltd/fe-lib/hooks'
import AnimatedLogo from '@wellbeingxltd/fe-lib/images/dist/logo/AnimatedLogo.gif'
import { AnalyticsActions } from 'store/models/analytics'
import TransactionsActions from 'store/models/transactions'
import TicketsActions from 'store/models/tickets'
import Button from 'components/Button'
import Label from 'components/Label'
import { ShippingBlock } from './ShippingBlock'
import './style.scss'

const Transaction = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const params = useParams()
    const { id } = params

    const cancelConfirmation = useConfirm({ title: 'Are you sure?', text: 'You want to mark this order as returned' })

    const { transactions, openedTransaction } = useSelector(GET_STORE.TRANSACTIONS)
    const { tickets, loadingTickets } = useSelector(GET_STORE.TICKETS)

    const [transaction, updateTransaction] = useState(openedTransaction)
    const [otherCompanyInput, toggleOtherCompanyInput] = useState(false)
    const [noTrackingNumber, setNoTrackingNumber] = useState(false)
    const [openedTicket, setOpenedTicket] = useState(false)
    const [error, setError] = useState('')
    const [deliveryCompany, setDeliveryCompany] = useState('')
    const [isShopifyTransaction, setIsShopifyTransaction] = useState(false)

    useEffect(() => {
        document.title = 'Transaction | Betterspace Supplier'
        if (!openedTransaction) {
            if (_.isEmpty(transactions)) {
                const supplier = JSON.parse(localStorage.getItem('supplier'))
                const { supplierId } = supplier
                dispatch(TransactionsActions.getTransactions(supplierId))
            } else {
                updateTransaction(transactions.find(item => item.transactionId === id))
            }
        }
        dispatch(AnalyticsActions.postAnalytics('[Transaction] componentDidMount', history.location.pathname))
        return () => {
            dispatch(TransactionsActions.openTransaction(null))
        }
    }, [transactions, openedTransaction, id])

    useEffect(() => {
        dispatch(TicketsActions.getTickets())
    }, [id])

    useEffect(() => {
        if (tickets.length) {
            const ticket = tickets.find(item => item.transactionId === id)
            if (ticket && ticket.ticketStatus === 'Ticket Created') setOpenedTicket(true)
        }
        const dCompany = transaction?.deliveryCompany
        const isOther = !DELIVERY_COMPANIES.includes(dCompany)
        if (isOther) {
            setDeliveryCompany(dCompany)
            toggleOtherCompanyInput(true)
        }
    }, [tickets, transaction, id])

    useEffect(() => {
        if (transaction) {
            setIsShopifyTransaction(!!JSON.parse(transaction.ApiIntegration)?.shopify)
        }
    }, [transaction])

    const cancelHandler = async e => {
        e.preventDefault()
        if (await cancelConfirmation()) {
            setError('')
            dispatch(
                TransactionsActions.cancelOrder(
                    {
                        transactionId: transaction.transactionId,
                        userId: transaction.userId,
                        state: 'CANCELLED',
                    },
                    (message, type) => {
                        Alert(message, type)
                        updateTransaction({ ...transaction, state: 'CANCELLED' })
                    }
                )
            )
        }
    }

    if (transaction == null || loadingTickets) {
        return (
            <div className="centerLoader">
                <img src={AnimatedLogo} width="128px" alt="Full screen loading spinner" />
            </div>
        )
    }

    return (
        <section className="supplierSection">
            <div className="supplierTransactionBackButton" onClick={() => history.goBack()}>
                <IconContext.Provider value={{ className: 'supplierTransactionChevronLeft' }}>
                    <IoIosArrowBack />
                </IconContext.Provider>
                <span>Back to Orders</span>
            </div>

            <div className="supplierTransactionHeader">
                <img src={transaction.imageThumbS} className="supplierTransactionThumb" alt="" />
                <div className="supplierTransactionHeaderRight">
                    <span
                        className={`supplierTransactionsPill ${transaction.state === 'CONFIRMED' && 'supplierTransactionsPill--dispatched'} ${
                            transaction.state === 'CANCELLED' && 'supplierTransactionsPill--cancelled'
                        }`}
                    >
                        {getTransactionStateText(transaction)}
                    </span>
                    <h1>
                        <span className="supplierTransactionHeaderTextLabel">Order/Transaction ID:</span>
                        <span>{transaction.transactionId}</span>
                    </h1>
                </div>
            </div>

            <div className="supplierTransactionContent">
                <div
                    className={`supplierTransactionStep ${transaction.state === 'CONFIRMED' && 'supplierTransactionStep--dispatched'} ${
                        transaction.state === 'CANCELLED' && 'supplierTransactionStep--cancelled'
                    }`}
                    data-step="1"
                >
                    <h2>Order</h2>

                    <table>
                        <tbody>
                            <tr>
                                <th>Title:</th>
                                <td>{transaction.title}</td>
                            </tr>
                            <tr>
                                <th>Description:</th>
                                <td>{transaction.priceDescription}</td>
                            </tr>
                            <tr>
                                <th>SKU:</th>
                                <td>{transaction.sku}</td>
                            </tr>
                            <tr>
                                <th>Price:</th>
                                <td>{formatPrice(transaction.price)}</td>
                            </tr>
                            <tr>
                                <th>Date of order:</th>
                                <td>{moment(transaction.date).format('DD/MM/YYYY HH:mm')}</td>
                            </tr>
                            <tr>
                                <th>Estimated delivery date:</th>
                                <td data-cy="estimatedDeliveryDate">
                                    <p>
                                        Users will expect to receive their order{' '}
                                        <strong>
                                            on or before{' '}
                                            {business
                                                .addWeekDays(moment(transaction.date), transaction.deliveryEstimatedDuration)
                                                .format('DD/MM/YYYY')}
                                        </strong>{' '}
                                        ({transaction.deliveryEstimatedDuration} working days from date of order).
                                    </p>
                                    <p>
                                        If there will be any delays, please contact the user as soon as possible by{' '}
                                        <Link to={`/tickets/${transaction.transactionId}`}>clicking here to go to the manage tickets page</Link> and
                                        then click the "Email user" button.
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ShippingBlock {...{ transaction }} />

                <div
                    className={`supplierTransactionStep ${transaction.state === 'CONFIRMED' && 'supplierTransactionStep--dispatched'} ${
                        transaction.state === 'CANCELLED' && 'supplierTransactionStep--cancelled'
                    }`}
                    data-step="3"
                >
                    <h2>Tracking</h2>
                    {openedTicket && (
                        <span className="opened-ticket-warning">
                            Warning, there is an open ticket from the user about this order. Please check your email before proceeding.
                        </span>
                    )}
                    <Label className="inputLabel">Delivery Company*</Label>
                    <select
                        value={otherCompanyInput ? 'Other' : transaction.deliveryCompany}
                        defaultValue={otherCompanyInput ? 'Other' : transaction.deliveryCompany}
                        onChange={val => {
                            if (val.target.value === 'Other') {
                                toggleOtherCompanyInput(true)
                                updateTransaction({ ...transaction, deliveryCompany: '' })
                            } else {
                                toggleOtherCompanyInput(false)
                                updateTransaction({ ...transaction, deliveryCompany: val.target.value })
                            }
                        }}
                        disabled={transaction.state === 'CANCELLED'}
                    >
                        <option disabled value="">
                            -- Choose --
                        </option>
                        {DELIVERY_COMPANIES.map(deliveryCompany => (
                            <option value={deliveryCompany}>{deliveryCompany}</option>
                        ))}
                        <option value="Other">Other</option>
                    </select>

                    {otherCompanyInput && (
                        <input
                            placeholder="Company name"
                            type="text"
                            value={deliveryCompany}
                            onChange={val => updateTransaction({ ...transaction, deliveryCompany: val.target.value })}
                        />
                    )}

                    <Label className="inputLabel" htmlFor="deliveryTrackingNumber">
                        Delivery Tracking Number*
                    </Label>
                    <input
                        id="deliveryTrackingNumber"
                        type="text"
                        placeholder="e.g. YCR123456789"
                        onChange={val => updateTransaction({ ...transaction, deliveryTrackingNumber: val.target.value })}
                        value={transaction.deliveryTrackingNumber}
                        disabled={transaction.state === 'CANCELLED' || noTrackingNumber}
                        style={{ marginBottom: '16px' }}
                    />

                    <div className="trackingNumberCheckbox">
                        <input
                            type="checkbox"
                            id="noTracking"
                            onChange={val => {
                                val.target.checked && updateTransaction({ ...transaction, deliveryTrackingNumber: '' })
                                setNoTrackingNumber(val.target.checked)
                            }}
                            disabled={transaction.state === 'CANCELLED'}
                        />
                        <Label htmlFor="noTracking">No tracking number available</Label>
                    </div>

                    {transaction.state === 'PENDING' && (
                        <Button
                            active
                            disabled={!transaction.deliveryCompany || (!transaction.deliveryTrackingNumber && !noTrackingNumber)}
                            onClick={() => {
                                if (!transaction.deliveryCompany && !transaction.deliveryTrackingNumber) {
                                    setError('Please select the delivery company.')
                                } else {
                                    setError('')
                                    dispatch(TransactionsActions.updateTransaction('CONFIRMED', transaction, history))
                                }
                            }}
                            className="supplierTransactionStepButton"
                            cta
                            variant={
                                !transaction.deliveryCompany || (!transaction.deliveryTrackingNumber && !noTrackingNumber) ? 'disabled' : 'default'
                            }
                        >
                            Mark as Dispatched
                        </Button>
                    )}

                    {transaction.state === 'CONFIRMED' && (
                        <Button
                            active
                            disabled={!transaction.deliveryCompany || (!transaction.deliveryTrackingNumber && !noTrackingNumber)}
                            onClick={() => {
                                setError('')
                                dispatch(TransactionsActions.updateTransaction('CONFIRMED', transaction, history))
                            }}
                            className="supplierTransactionStepButton"
                            cta
                            variant={
                                !transaction.deliveryCompany || (!transaction.deliveryTrackingNumber && !noTrackingNumber) ? 'disabled' : 'default'
                            }
                        >
                            Update Tracking Details
                        </Button>
                    )}

                    {error && (
                        <p className="error supplierTransactionError" role="alert">
                            <IconContext.Provider value={{ className: 'errorIcon' }}>
                                <IoMdCloseCircle />
                            </IconContext.Provider>
                            {error}
                        </p>
                    )}
                </div>
            </div>

            {transaction.state !== 'CANCELLED' && (
                <div className="supplierTransactionFooter">
                    <h2>Returns</h2>

                    {isShopifyTransaction ? (
                        <p>Shopify orders cannot be refunded automatically through Betterspace. Please open a support ticket instead.</p>
                    ) : (
                        <p>
                            <IconContext.Provider value={{ className: 'supplierTransactionInfoIcon' }}>
                                <IoMdUndo />
                            </IconContext.Provider>
                            If this order has been returned, please{' '}
                            <a href="#" onClick={cancelHandler}>
                                click here to mark it as returned/cancelled
                            </a>
                            . This will automatically refund the user.
                        </p>
                    )}
                </div>
            )}

            <div className="supplierTransactionFooter">
                <h2>Problems fulfilling</h2>
                <p>
                    <IconContext.Provider value={{ className: 'supplierTransactionInfoIcon' }}>
                        <IoMdInformationCircleOutline />
                    </IconContext.Provider>
                    If you have a problem fulfilling this order, please contact the user as soon as possible by{' '}
                    <Link to={`/tickets/${transaction.transactionId}`}>clicking here to go to the manage tickets page</Link> and then click the "Email
                    user" button.
                </p>
            </div>
        </section>
    )
}

export default Transaction
